import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon } from '@utils/icons';
import type { ITakeoffElement } from '@interfaces/ICadTakeoff';
import {
  elementGroups,
  type ClaimUnitKey,
  type IElementGroup,
  type IFloorBand,
  type ITagPart,
} from '../../stock/claimElements';
import { PickerSection } from './StockElementPickerRows';
import styles from './StockElementPicker.module.css';

/** What every section needs from the picker, whichever band it sits in —
 *  handed down as one object so a band is not a second copy of the picker's
 *  prop list. */
export interface ISectionContext {
  kind: string;
  unitLabel: string;
  parts: ReadonlyMap<string, ITagPart>;
  collapsible: boolean;
  taken: ReadonlyMap<string, string>;
  chosen: ReadonlySet<string>;
  unitKey: ClaimUnitKey | null;
  /** The section keys the user folded — `{scope}{typeName}`, so the same
   *  type on two floors folds independently. */
  closedSections: ReadonlySet<string>;
  onToggleSection: (key: string) => void;
  /** The group keys expanded — scoped the same way (`groupKey`). */
  open: ReadonlySet<string>;
  onToggleOpen: (key: string) => void;
  onToggle: (globalId: string) => void;
  onToggleGroup: (group: IElementGroup) => void;
  onCount: (group: IElementGroup, count: number) => void;
}

interface IPickerSectionsProps {
  /** Prefix for the section and group keys — the band's floor, '' with none. */
  scope: string;
  /** The elements in view — the filter applied. */
  elements: ITakeoffElement[];
  ctx: ISectionContext;
}

/** The type sections of one list of elements — `elementGroups` called on it
 *  alone, so a band's sections and groups are its own. */
export function PickerSections({ scope, elements, ctx }: IPickerSectionsProps) {
  const { taken, chosen, unitKey } = ctx;
  const sections = useMemo(() => elementGroups(elements, taken, chosen, unitKey), [elements, taken, chosen, unitKey]);
  return (
    <>
      {sections.map((section) => {
        const key = `${scope}${String(section.typeName)}`;
        return (
          <PickerSection
            key={key}
            scope={scope}
            section={section}
            kind={ctx.kind}
            unitLabel={ctx.unitLabel}
            parts={ctx.parts}
            collapsible={ctx.collapsible}
            sectionOpen={!ctx.closedSections.has(key)}
            onToggleSection={() => ctx.onToggleSection(key)}
            open={ctx.open}
            onToggleOpen={ctx.onToggleOpen}
            onToggle={ctx.onToggle}
            onToggleGroup={ctx.onToggleGroup}
            onCount={ctx.onCount}
          />
        );
      })}
    </>
  );
}

interface IPickerBandProps {
  band: IFloorBand;
  /** The band's elements in view — the filter applied. A band it leaves
   *  nothing of is not shown. */
  shown: ITakeoffElement[];
  open: boolean;
  onToggle: () => void;
  ctx: ISectionContext;
}

/** One floor's band: a folding header naming the floor, how many of the
 *  kind it holds and how many of those are taken, with the floor's own type
 *  sections under it while open. */
export function PickerBand({ band, shown, open, onToggle, ctx }: IPickerBandProps) {
  const { t } = useTranslation();
  if (shown.length === 0) return null;
  const takenCount = band.elements.filter((el) => ctx.taken.has(el.globalId)).length;
  return (
    <>
      <button type="button" className={styles.band} aria-expanded={open} onClick={onToggle}>
        <span>
          {t('scheduler.panel.stock.floorBand', { floor: band.floorName, count: band.elements.length, kind: ctx.kind.toLowerCase() })}
          {takenCount > 0 && (
            <>
              {' '}
              <span className={styles.bandTaken}>{t('scheduler.panel.stock.floorBandTaken', { count: takenCount })}</span>
            </>
          )}
        </span>
        <span className={styles.caret} data-collapsed={!open}>
          <ChevronDownIcon size={14} />
        </span>
      </button>
      {open && <PickerSections scope={`${band.floorId}|`} elements={shown} ctx={ctx} />}
    </>
  );
}
